import { useNumbers } from '../../lib/api/hooks-tms.js';
import { Field } from '../../components/ui/Form.js';

/**
 * The number a seat presents when it calls out: chosen, never typed.
 *
 * Free text here was a fault, not an inconvenience. A carrier refuses an
 * outbound call whose CLI is not a number on the account, and refuses it with a
 * 403 that names nothing -- so a typo produced calls that failed for a reason
 * visible nowhere in this platform, and a number belonging to another customer
 * would have been accepted by our own switch, which is CLI spoofing. The API
 * enforces the same rule; this is the half that makes it impossible to get
 * wrong rather than merely caught.
 *
 * Withholding lives in the same control rather than beside it as a second
 * toggle. "Which number" and "no number" are one decision -- a seat cannot be
 * both -- and the old pair let you set a caller ID and then withhold it, two
 * saved settings describing one call, one of which did nothing.
 *
 * Shared because the same decision appears on anything that can originate a
 * call: hunt lists and queues present a number too, and the next one to need it
 * should not grow a second, differently-behaved copy.
 */

/** Not a number, so it can never collide with one. */
const ANON = '__anonymous__';

export function CallerIdSelect({
  tenantId, number, anonymous, onChange, id = 'cid-number', label = 'Caller ID number',
}: {
  tenantId: string;
  /** The stored `outbound_caller_id_number`. */
  number: string | null;
  /** The stored `hide_caller_id`. */
  anonymous: boolean;
  onChange: (next: { number: string | null; anonymous: boolean }) => void;
  id?: string;
  label?: string;
}) {
  const numbers = useNumbers(tenantId);

  /**
   * Assigned, and nothing looser.
   *
   * A reserved number is not in service and a port-in is still answered by the
   * losing carrier, so offering either would offer a call that fails later --
   * and the API refuses them for the same reason.
   */
  const assigned = (numbers.data?.data ?? [])
    .filter((n) => n.status === 'assigned')
    .map((n) => n.e164)
    .sort();

  /**
   * A stored number that is no longer on the account.
   *
   * Kept as an option rather than silently dropped: losing a saved setting
   * because a list loaded differently is worse than showing it and saying it is
   * wrong. It reads as an error because it is one -- the carrier will refuse it.
   */
  const stale = !anonymous && !!number && !assigned.includes(number);

  const value = anonymous ? ANON : (number ?? '');

  /**
   * Three different "no numbers here", and they are not interchangeable.
   *
   * A role without `number:read` gets a 403, which looks identical to an empty
   * inventory unless it is said out loud -- and telling someone their account has
   * no numbers when it has twelve sends them to the wrong place to fix it.
   */
  const hint = numbers.isLoading ? undefined
    : numbers.error
      ? 'Numbers could not be loaded, so only the choices above are offered.'
    : assigned.length === 0
      ? 'This account has no numbers assigned yet, so calls present the trunk’s'
        + ' default caller ID.'
      : 'Account default presents the trunk’s own number. Withheld sends the'
        + ' call as private — a carrier that cannot withhold presents the number'
        + ' anyway.';

  return (
    <Field label={label} htmlFor={id}
      {...(stale ? { error: `${number} is not assigned to this account — a carrier will refuse calls presenting it.` } : {})}
      {...(hint ? { hint } : {})}>
      <select id={id} className="arc-select" value={value}
        disabled={numbers.isLoading}
        onChange={(e) => {
          const v = e.target.value;
          if (v === ANON) onChange({ number: null, anonymous: true });
          else onChange({ number: v || null, anonymous: false });
        }}>
        <option value="">— account default —</option>
        <option value={ANON}>Withheld (anonymous)</option>
        {stale ? <option value={number!}>{number} — not on this account</option> : null}
        {assigned.map((e164) => <option key={e164} value={e164}>{e164}</option>)}
      </select>
    </Field>
  );
}
